import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams, Navigate } from 'react-router-dom';
import Markdown from 'react-markdown';
import blogPosts from '../data/blog/posts';
import appIcon from '../assets/icon.png';
import authorPhoto from '../assets/developer-photo.png';
import './Blog.css';

const estimateReadTime = (content) => {
  const words = content.trim().split(/\s+/).length;
  return Math.max(1, Math.round(words / 230));
};

const BlogPost = () => {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const jsonLdArticle = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": post.description,
    "datePublished": post.date,
    "author": {
      "@type": "Organization",
      "name": post.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "Forecast",
      "url": "https://tryforecast.app"
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://tryforecast.app/blog/${post.slug}`
    }
  };

  return (
    <div className="blog-page">
      <Helmet>
        <title>{post.title} | Forecast Blog</title>
        <meta name="description" content={post.description} />
        <link rel="canonical" href={`https://tryforecast.app/blog/${post.slug}`} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.description} />
        <meta property="og:url" content={`https://tryforecast.app/blog/${post.slug}`} />
        <meta property="og:type" content="article" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.description} />
        <script type="application/ld+json">{JSON.stringify(jsonLdArticle)}</script>
      </Helmet>

      <nav className="blog-nav">
        <div className="blog-nav-inner">
          <Link to="/" className="blog-nav-brand">
            <img src={appIcon} alt="Forecast" />
            <span>Forecast</span>
          </Link>
          <div className="blog-nav-links">
            <Link to="/blog" className="blog-nav-link">Blog</Link>
            <Link to="/" className="blog-nav-cta">Try Forecast</Link>
          </div>
        </div>
      </nav>

      <div className="blog-post-hero">
        <Link to="/blog" className="blog-post-back">
          &larr; All posts
        </Link>

        <h1 className="blog-post-title">{post.title}</h1>

        <div className="blog-post-meta">
          <img src={authorPhoto} alt={post.author} className="blog-post-avatar-img" />
          <div className="blog-post-meta-text">
            <span className="blog-post-author">{post.author}</span>
            <span className="blog-post-date">
              {new Date(post.date + 'T12:00:00').toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              })}
              {' '}&middot;{' '}
              {estimateReadTime(post.content)} min read
            </span>
          </div>
        </div>
      </div>

      <article className="blog-post-content">
        <Markdown>{post.content}</Markdown>
      </article>

      <div className="blog-cta">
        <div className="blog-cta-inner">
          <h3 className="blog-cta-title">See your financial future</h3>
          <p className="blog-cta-text">
            Forecast shows your projected bank balance for every day ahead. No spreadsheets, no guesswork.
          </p>
          <Link to="/" className="blog-cta-button">
            Try Forecast Free
          </Link>
        </div>
      </div>

      <footer className="blog-footer">
        <span className="blog-footer-text">&copy; {new Date().getFullYear()} Forecast</span>
        <div className="blog-footer-links">
          <Link to="/privacy-policy" className="blog-footer-link">Privacy</Link>
          <Link to="/terms-of-service" className="blog-footer-link">Terms</Link>
          <Link to="/support" className="blog-footer-link">Support</Link>
        </div>
      </footer>
    </div>
  );
};

export default BlogPost;
